// components/private/order/KonfirmRegularView.tsx

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    CheckCircle2,
    User,
    UserCircle,
    Package,
    Info,
    Mail,
    Phone,
    Shirt,
    Droplets,
    Building2,
    MapPin,
    type LucideIcon
} from "lucide-react";
import type { RegistrationItem } from "@/types/registration";
import type { UserData } from "@/types/pic";
import { useUser } from "@/context/useUser";

interface KonfirmRegularViewProps {
    data: RegistrationItem;
    user: UserData | null;
}

// Helper Format Rupiah (Mendukung tipe Number dan String dari API)
const formatRupiah = (value: number | string) => {
    // Pastikan nilainya diubah menjadi angka terlebih dahulu
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;

    // Jika hasilnya bukan angka valid (NaN), kembalikan Rp. 0
    if (isNaN(numericValue)) return "Rp. 0";

    return "Rp. " + new Intl.NumberFormat("id-ID").format(numericValue);
};

// Sub-component untuk Baris Info (Font Original)
const InfoItem = ({
    icon: Icon,
    label,
    value,
    isCurrency = false
}: {
    icon: LucideIcon;
    label: string;
    value?: string | number | null;
    isCurrency?: boolean
}) => (
    <div className="flex items-start space-x-4 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-full shrink-0">
            <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {label}
            </p>
            <p className="text-sm font-semibold text-foreground break-words">
                {/* Perbaikan pengecekan isCurrency di sini */}
                {isCurrency && value != null ? formatRupiah(value) : (value ?? "-")}
            </p>
        </div>
    </div>
);

export const KonfirmRegularView = ({ data, user }: KonfirmRegularViewProps) => {
    const { data: rawUser } = useUser();
    const participant = rawUser?.participant;

    return (
        <div className="w-full">
            <Card className="border-0 pt-0 shadow-xl bg-white dark:bg-gray-800 overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700">

                {/* Header */}
                <CardHeader className="bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-900 dark:to-blue-900/20 border-b pb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center space-x-3">
                            <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full ring-4 mt-10 ring-white dark:ring-slate-800">
                                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <CardTitle className="text-xl font-bold text-slate-800 dark:text-white pt-10">
                                    Konfirmasi Paket
                                </CardTitle>
                                <CardDescription>
                                    Tinjau paket dan data Anda sebelum melanjutkan pembayaran.
                                </CardDescription>
                            </div>
                        </div>
                        <div className="text-left sm:text-right">
                            <Badge variant="outline" className="px-3 py-1 mt-10 bg-white/50 dark:bg-black/20">
                                {data.registration_number || "REG-XXXX"}
                            </Badge>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="p-0">

                    {/* Section 1: Detail Paket (Banner Eksklusif & Menarik) */}
                    <div className="p-6">
                        {/* <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 mb-4 flex items-center">
                            <Package className="w-4 h-4 mr-2" /> Detail Paket Pilihan
                        </h4> */}

                        {/* Banner Card UI */}
                        <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800 dark:from-blue-700 dark:to-blue-900 rounded-2xl p-6 text-white shadow-lg shadow-blue-900/20">
                            {/* Dekorasi Background */}
                            <div className="absolute -right-4 -top-4 opacity-10 pointer-events-none">
                                <Package className="w-32 h-32" />
                            </div>

                            <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                                <div>
                                    <p className="text-blue-100/80 text-xs font-semibold uppercase tracking-wider mb-1">
                                        Nama Paket
                                    </p>
                                    <p className="text-xl font-bold">
                                        {data.package?.name || "-"}
                                    </p>
                                </div>
                                <div className="text-left md:text-right w-full md:w-auto pt-4 md:pt-0 border-t border-blue-500/30 md:border-0">
                                    <p className="text-blue-100/80 text-xs font-semibold uppercase tracking-wider mb-1">
                                        Total Pembayaran
                                    </p>
                                    <p className="text-3xl font-black tracking-tight text-white">
                                        {formatRupiah(data.package?.price || 0)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="h-px bg-slate-200 dark:bg-slate-700 mx-6" />

                    {/* Section 2: Data Peserta */}
                    <div className="p-6">
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 mb-4 flex items-center">
                            <User className="w-4 h-4 mr-2" /> Data Peserta
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InfoItem icon={User} label="Nama Lengkap" value={participant?.full_name || user?.name} />
                            <InfoItem icon={UserCircle} label="Jenis Kelamin" value={participant?.gender} />
                            <InfoItem icon={Droplets} label="Golongan Darah" value={participant?.blood_type} />
                            <InfoItem icon={Shirt} label="Ukuran Baju" value={participant?.clothing_size} />
                        </div>
                    </div>

                    <div className="h-px bg-slate-200 dark:bg-slate-700 mx-6" />

                    {/* Section 3: Kontak & Institusi */}
                    <div className="p-6">
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 mb-4 flex items-center">
                            <Mail className="w-4 h-4 mr-2" /> Kontak & Institusi
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InfoItem icon={Mail} label="Email Terdaftar" value={participant?.email || user?.email} />
                            <InfoItem icon={Phone} label="No. WhatsApp" value={participant?.phone_mobile} />
                            <InfoItem icon={Building2} label="Nama Institusi" value={participant?.institution} />
                            <InfoItem icon={MapPin} label="Alamat Institusi" value={participant?.institution_address} />
                        </div>
                    </div>

                    {/* Footer Section */}
                    <div className="p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <Info className="w-5 h-5 text-blue-500 shrink-0" />
                            <div className="text-sm text-muted-foreground">
                                <span className="block font-medium text-foreground">Informasi Pendaftaran</span>
                                Pastikan data diri dan pilihan paket Anda sudah sesuai sebelum melanjutkan pembayaran.
                            </div>
                        </div>
                    </div>

                </CardContent>
            </Card>
        </div>
    );
};